"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { ResponsiveImage } from "@/components/ResponsiveImage";

interface CarGalleryProps {
  photos: string[];
  alt: string;
  /**
   * Intrinsic master widths parallel to `photos` (from Car.photoWidths). Optional — absent for
   * un-backfilled cars, in which case ResponsiveImage falls back to the bare JPEG master.
   */
  photoWidths?: number[];
}

export default function CarGallery({ photos, alt, photoWidths }: CarGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div>
      {/* Main photo */}
      <div className="relative aspect-[16/9] overflow-hidden rounded-xl bg-surface">
        <ResponsiveImage
          masterUrl={photos[activeIndex]}
          intrinsicWidth={photoWidths?.[activeIndex]}
          alt={`${alt} — фото ${activeIndex + 1}`}
          fill
          className="object-contain"
          sizes="(max-width: 768px) 100vw, 60vw"
          priority
        />
      </div>

      {/* Thumbnails — scroll-snap on mobile */}
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none">
        {photos.map((photo, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            aria-label={`Показать фото ${i + 1} из ${photos.length}`}
            aria-current={i === activeIndex ? "true" : undefined}
            className={cn(
              "relative h-16 w-24 flex-shrink-0 snap-start overflow-hidden rounded-lg transition-all sm:h-20 sm:w-28",
              activeIndex === i
                ? "ring-2 ring-primary ring-offset-2"
                : "opacity-60 hover:opacity-100"
            )}
          >
            <ResponsiveImage
              masterUrl={photo}
              intrinsicWidth={photoWidths?.[i]}
              alt={`${alt} — миниатюра ${i + 1}`}
              fill
              className="object-cover"
              sizes="112px"
              priority={i === 0}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
